import useVideoControl from '@/hooks/useVideoControl';
import React, { useState, useCallback } from 'react';
import { PlayCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import * as Sentry from '@sentry/react';
import Preloader from '../Preloader';
import { VideoStatusOverlay, SlowNetworkHint } from './VideoStatusOverlay';
import { VideoPlayerSlider } from './VideoPlayerSlider';
import { useMeasure } from '@uidotdev/usehooks';
import { useVideoDownloadTracking } from './useVideoDownloadTracking';
import { useSlowNetworkWarning } from './useSlowNetworkWarning';
import { useVideoRetry } from './useVideoRetry';
import { isSafari } from '@/constants';

// ── Types ────────────────────────────────────────────────────────────────

interface VideoPlayerProps {
  src?: string;
  onSetCurRef?: (ref: HTMLVideoElement | null) => void;
  /** Defaults to `true`. Set `false` for tap-to-play videos (e.g. the InterviewPreview answer grid). */
  autoPlay?: boolean;
  style?: React.CSSProperties;
  PlayIcon?: React.ReactNode;
  ReplayIcon?: React.ReactNode;
  width?: string;
  height?: string;
  type?: 'video/webm' | 'video/mp4';
  controls?: boolean;
  children?: React.ReactNode;
  /** Show a "slow connection" overlay if video hasn't loaded within 7 seconds. */
  showSlowNetworkWarning?: boolean;
  /**
   * Show a dimmed "Tap to play" overlay with a large play button while paused.
   * Use for videos that can't autoplay (e.g. the WebM answers on iOS, which
   * render a blank frame until the user taps) so the player doesn't look like
   * a confusing white screen.
   */
  showPlayOverlay?: boolean;
}

// ── Component ────────────────────────────────────────────────────────────

const VideoPlayer = React.forwardRef<HTMLVideoElement, VideoPlayerProps>(
  (
    {
      src,
      onSetCurRef,
      autoPlay = true,
      style,
      PlayIcon,
      ReplayIcon,
      width = '330px',
      height,
      controls,
      children,
      showSlowNetworkWarning: showSlowNetworkWarningProp = false,
      showPlayOverlay = false,
    },
    ref
  ) => {
    const {
      isPlaying,
      play,
      setCurRef,
      isReady,
      curTime,
      duration,
      curRef,
    } = useVideoControl();
    const [wrapperRef, wrapperSize] = useMeasure();

    const [rotate, setRotate] = useState(false);
    const [aspectRatioCSS, setAspectRatioCSS] = useState<number[] | undefined>(
      [3, 4] // Default to 3:4 aspect ratio
    );

    // ── Sentry download tracking ──────────────────────────────────────
    useVideoDownloadTracking(curRef, src);

    // ── Slow network warning ──────────────────────────────────────────
    const { showWarning: showSlowNetwork, dismiss: dismissSlowNetwork } =
      useSlowNetworkWarning(isReady, src, showSlowNetworkWarningProp);

    // ── Retry on error ───────────────────────────────────────────
    const { hasError, manualRetry } = useVideoRetry(curRef, src);

    const handleLoadedMetadata = useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement>) => {
        const video = e.currentTarget;
        // Safari with preload="metadata" suspends after metadata and won't fetch
        // media data on its own, so an autoplay video has nothing to play and
        // never starts. A tiny seek forces it to fetch + decode the first frame,
        // which lets autoplay proceed (and paints a poster-like first frame).
        //
        // Gate strictly on `autoPlay`: ONLY the single MP4 question/intro video
        // gets this (one video on screen = one decode). The InterviewPreview
        // answer grid is tap-to-play and renders 3-4 WebM videos at once; forcing
        // a decode per video allocates a software WebM decoder for each one and
        // exhausts the media-resource ceiling — on iOS (code 2 evictions) AND on
        // older/Intel desktop Safari (videos fail to load at all). There is no
        // safe way to fake a poster here without a real thumbnail image; see
        // README.md and memory `interview-ios-webm-concurrency`.
        if (autoPlay && isSafari) {
          video.currentTime = 0.1;
        }
        // Decide if we need to rotate
        const shouldRotate = video.videoWidth > video.videoHeight;
        setRotate(shouldRotate);

        // Compute CSS aspect-ratio string:
        const ratio = shouldRotate
          ? [video.videoHeight, video.videoWidth]
          : [video.videoWidth, video.videoHeight];
        setAspectRatioCSS(ratio);

        Sentry.addBreadcrumb({
          category: 'video-player',
          message: 'Video metadata loaded',
          level: 'info',
          data: {
            src,
            videoWidth: video.videoWidth,
            videoHeight: video.videoHeight,
            duration: video.duration,
            rotated: shouldRotate,
          },
        });
      },
      [src, autoPlay]
    );

    // Dynamic styles — only values that depend on runtime data
    const wrapperDynamicStyle: React.CSSProperties = {
      width,
      ...(height
        ? { height }
        : aspectRatioCSS?.length
        ? {
            aspectRatio: aspectRatioCSS.join(' / '),
            paddingTop: Math.floor((aspectRatioCSS[1] / aspectRatioCSS[0]) * 100) + '%',
          }
        : {}),
      ...style,
    };

    const videoStyle: React.CSSProperties = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transformOrigin: 'center center',
      objectFit: 'cover',
      maxWidth: rotate ? 'unset' : '100%',
      transform: `translate(-50%, -50%)${rotate ? ' rotate(90deg)' : ''}`,
      ...(rotate && {
        height: wrapperSize.width + 'px',
        width: wrapperSize.height + 'px',
      }),
    };

    return (
      <div
        className="relative overflow-hidden"
        style={wrapperDynamicStyle}
        ref={wrapperRef}
      >
        <video
          ref={(node) => {
            setCurRef(node);
            onSetCurRef?.(node);
            if (typeof ref === 'function') ref(node);
            else if (ref) ref.current = node;
          }}
          src={src}
          preload="metadata"
          playsInline
          autoPlay={autoPlay}
          onClick={play}
          onLoadedMetadata={handleLoadedMetadata}
          style={videoStyle}
        >
          {children}
        </video>

        {!isReady && !hasError && (
          <div className="absolute inset-0 z-[4] flex flex-col items-center justify-center gap-2 bg-black/50 p-6 text-center">
            <Preloader />
            {showSlowNetwork && <SlowNetworkHint onDismiss={dismissSlowNetwork} />}
          </div>
        )}

        {hasError && (
          <VideoStatusOverlay
            hasError={hasError}
            onRetry={manualRetry}
          />
        )}

        {controls && (
          <div className="absolute bottom-0 z-[2] w-full">
            <VideoPlayerSlider
              currentTime={curTime}
              duration={duration}
              onTimeChange={(time) => {
                if (curRef) curRef.currentTime = time;
              }}
            />
          </div>
        )}

        {showPlayOverlay ? (
          // Dimmed "tap to play" affordance for videos that can't autoplay —
          // masks the blank pre-decode frame. pointer-events-none so the tap
          // falls through to the <video> onClick={play} (the gesture iOS needs).
          !isPlaying && isReady && (
            <div className="pointer-events-none absolute inset-0 z-[3] flex flex-col items-center justify-center gap-2 bg-black/60">
              {curTime < duration ? (
                <>
                  <PlayCircleOutlined className="animate-pulse-size text-[64px] text-white drop-shadow-lg" />
                  <span className="text-sm font-medium text-white drop-shadow">
                    Tap to play
                  </span>
                </>
              ) : (
                <>
                  <ReloadOutlined className="text-[56px] text-white drop-shadow-lg" />
                  <span className="text-sm font-medium text-white drop-shadow">
                    Replay
                  </span>
                </>
              )}
            </div>
          )
        ) : (
          <div className="pointer-events-none absolute inset-0 z-[3] flex items-center justify-center">
            {!isPlaying && isReady && curTime < duration && (
              PlayIcon || (
                <PlayCircleOutlined
                  className="animate-pulse-size text-[40px] text-white"
                />
              )
            )}
            {!isPlaying && isReady && curTime >= duration && (
              ReplayIcon || (
                <ReloadOutlined className="text-[40px] text-white" />
              )
            )}
          </div>
        )}
      </div>
    );
  }
);

export default VideoPlayer;
